import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { VisibilityEditor } from "./visibility-editor";
import type { TourAudienceCriteria, TourAudienceRecord } from "./types";

interface AudienceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audience: TourAudienceRecord | null;
}

export function AudienceDialog({ open, onOpenChange, audience }: AudienceDialogProps) {
  const { toast } = useToast();
  const isEdit = !!audience;
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [criteria, setCriteria] = useState<TourAudienceCriteria>({});
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    if (open) {
      setSlug(audience?.slug ?? "");
      setLabel(audience?.label ?? "");
      setDescription(audience?.description ?? "");
      setCriteria(audience?.criteria ?? {});
      setIsDefault(audience?.isDefault ?? false);
    }
  }, [open, audience]);

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        slug: slug.trim(),
        label: label.trim(),
        description: description.trim() || null,
        criteria,
        isDefault,
      };
      if (isEdit && audience) {
        return apiRequest("PATCH", `/api/admin/tour-audiences/${audience.id}`, body);
      }
      return apiRequest("POST", "/api/admin/tour-audiences", body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/tour-audiences"] });
      toast({ title: isEdit ? "Audience updated" : "Audience created" });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  const slugValid = /^[a-z][a-z0-9-]*$/.test(slug);
  const canSubmit = slug.length > 0 && label.length > 0 && slugValid && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit audience" : "New audience"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="aud-slug">Slug</Label>
            <Input
              id="aud-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. all-staff"
              disabled={isEdit}
              data-testid="input-audience-slug"
            />
            {!slugValid && slug.length > 0 && (
              <p className="text-xs text-destructive">
                Lowercase letters, digits, and hyphens only.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="aud-label">Label</Label>
            <Input
              id="aud-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="input-audience-label"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="aud-desc">Description</Label>
            <Textarea
              id="aud-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="input-audience-description"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="aud-default">Default audience (fallback)</Label>
            <Switch
              id="aud-default"
              checked={isDefault}
              onCheckedChange={setIsDefault}
              data-testid="switch-audience-default"
            />
          </div>
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">Membership criteria</h3>
            <VisibilityEditor value={criteria} onChange={setCriteria} />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-audience-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            data-testid="button-audience-save"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
